import { transformacaoDoRisco } from '../../dominio/tabletop/encaixeRiscos'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useThree, type ThreeEvent } from '@react-three/fiber'
import { Plane, Raycaster, Vector2, Vector3, type Group } from 'three'
import type {
  ObjetoTabletop,
  TransformacaoTabletop,
} from '../../dominio/tabletop'
import { ponteiroArrastou, type PontoDeTela } from '../gestoPonteiro'
import {
  encaixarTransformacao,
  girarNoChao,
  moverNoChao,
  type ModoManipulacaoMesa,
  type PontoChao,
} from './manipulacaoMesa'

interface Props {
  objetos: readonly ObjetoTabletop[]
  posicionando: boolean
  passoPosicaoM?: number | null
  passoGiroGraus?: number | null
  aoTransformar?: (id: string, transformacao: TransformacaoTabletop) => void
  aoManipular?: (ativa: boolean) => void
  aoCancelarPosicionamento?: () => void
}
interface Gesto {
  id: string
  pointerId: number
  inicioTela: PontoDeTela
  inicioChao: PontoChao
  original: TransformacaoTabletop
  previa: TransformacaoTabletop
  modo: ModoManipulacaoMesa
  moveu: boolean
  controles: { enabled: boolean } | null
  orbitaAnterior: boolean
  ativo: boolean
  timer: ReturnType<typeof setTimeout> | null
}

export function useManipulacaoMesa3D(props: Props) {
  const { camera, gl, controls } = useThree()
  const atuais = useRef(props)
  atuais.current = props
  const grupos = useRef(new Map<string, Group>())
  const alca = useRef<Group>(null)
  const previaPosicao = useRef<Group>(null)
  const gesto = useRef<Gesto | null>(null)
  const pressao = useRef<(PontoDeTela & { pointerId: number }) | null>(null)
  const suprimirClique = useRef(false)
  const toques = useRef(new Set<number>())
  const multitoque = useRef(false)
  const plano = useMemo(() => new Plane(new Vector3(0, 1, 0), 0), [])
  const raio = useMemo(() => new Raycaster(), [])

  const pontoNoChao = useCallback(
    (evento: Pick<PointerEvent, 'clientX' | 'clientY'>): PontoChao | null => {
      const r = gl.domElement.getBoundingClientRect()
      if (!r.width || !r.height) return null
      raio.setFromCamera(
        new Vector2(
          ((evento.clientX - r.left) / r.width) * 2 - 1,
          -((evento.clientY - r.top) / r.height) * 2 + 1,
        ),
        camera,
      )
      const ponto = raio.ray.intersectPlane(plano, new Vector3())
      return ponto && Number.isFinite(ponto.x) && Number.isFinite(ponto.z)
        ? [ponto.x, ponto.z]
        : null
    },
    [gl, camera, raio, plano],
  )

  const aplicarPrevia = useCallback((id: string, t: TransformacaoTabletop) => {
    const grupo = grupos.current.get(id)
    if (!grupo) return
    grupo.position.set(...t.posicao)
    grupo.rotation.set(...t.rotacao)
    const tanque = atuais.current.objetos.find(o => o.id === id && o.tipo === 'caminhaoTanque')
    if (tanque) atuais.current.objetos.filter(o => o.idTanque === id).forEach(risco => {
      const efeito = grupos.current.get(risco.id)
      if (!efeito) return
      const pose = transformacaoDoRisco(risco.tipo, { ...tanque, transformacao: t }, risco.transformacao.escala)
      efeito.position.set(...pose.posicao)
      efeito.rotation.set(...pose.rotacao)
    })
    if (alca.current) {
      alca.current.position.set(t.posicao[0], t.posicao[1] + 0.06, t.posicao[2])
      alca.current.rotation.y = t.rotacao[1]
    }
  }, [])

  const terminar = useCallback(
    (cancelado: boolean) => {
      const g = gesto.current
      if (!g) return
      gesto.current = null
      if (g.timer) clearTimeout(g.timer)
      suprimirClique.current = g.moveu
      if (cancelado) aplicarPrevia(g.id, g.original)
      if (g.ativo && g.controles) g.controles.enabled = g.orbitaAnterior
      if (g.ativo) atuais.current.aoManipular?.(false)
      if (!cancelado && g.ativo && g.moveu)
        atuais.current.aoTransformar?.(g.id, g.previa)
    },
    [aplicarPrevia],
  )

  const iniciar = (
    id: string,
    modo: ModoManipulacaoMesa,
    evento: ThreeEvent<PointerEvent>,
  ) => {
    if (
      atuais.current.posicionando ||
      evento.button !== 0 ||
      evento.isPrimary === false ||
      toques.current.size > 1 ||
      gesto.current ||
      !atuais.current.aoTransformar
    )
      return
    const objeto = atuais.current.objetos.find((o) => o.id === id)
    if (!objeto || objeto.bloqueado || objeto.oculto) return
    const inicioChao = pontoNoChao(evento.nativeEvent)
    if (!inicioChao) return
    evento.stopPropagation()
    const controles =
      controls && 'enabled' in controls
        ? (controls as unknown as { enabled: boolean })
        : null
    gesto.current = {
      id,
      pointerId: evento.pointerId,
      inicioTela: { x: evento.clientX, y: evento.clientY },
      inicioChao,
      original: objeto.transformacao,
      previa: objeto.transformacao,
      modo,
      moveu: false,
      controles,
      orbitaAnterior: controles?.enabled ?? true,
      ativo: evento.pointerType !== 'touch',
      timer: null,
    }
    const g = gesto.current
    const ativar = () => {
      if (gesto.current !== g) return
      g.ativo = true
      if (controles) controles.enabled = false
      atuais.current.aoManipular?.(true)
    }
    if (g.ativo) ativar()
    else g.timer = setTimeout(ativar, 320)
  }

  const foiClique = (evento: ThreeEvent<MouseEvent>): boolean => {
    if (multitoque.current) return false
    if (suprimirClique.current) {
      suprimirClique.current = false
      return false
    }
    return !ponteiroArrastou(pressao.current, {
      x: evento.clientX,
      y: evento.clientY,
    })
  }

  useEffect(() => {
    const canvas = gl.domElement
    const pressionar = (e: PointerEvent) => {
      if (e.pointerType === 'touch') {
        toques.current.add(e.pointerId)
        multitoque.current = toques.current.size > 1
        if (multitoque.current) {
          terminar(true)
          return
        }
      } else multitoque.current = false
      if (e.button !== 0 || e.isPrimary === false) return
      pressao.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId }
      suprimirClique.current = false
    }
    const mover = (e: PointerEvent) => {
      const g = gesto.current
      const ponto = pontoNoChao(e)
      if (
        atuais.current.posicionando &&
        previaPosicao.current &&
        ponto &&
        (e.target === canvas ||
          (e.target instanceof Node && canvas.contains(e.target)))
      ) {
        previaPosicao.current.visible = true
        const t = encaixarTransformacao(
          {
            posicao: [ponto[0], 0.055, ponto[1]],
            rotacao: [0, 0, 0],
            escala: [1, 1, 1],
          },
          atuais.current.passoPosicaoM,
        )
        previaPosicao.current.position.set(...t.posicao)
      }
      if (
        !g ||
        e.pointerId !== g.pointerId ||
        !ponto ||
        (!g.moveu &&
          !ponteiroArrastou(g.inicioTela, { x: e.clientX, y: e.clientY }))
      )
        return
      if (!g.ativo) {
        terminar(true)
        return
      }
      g.previa =
        g.modo === 'mover'
          ? moverNoChao(g.original, g.inicioChao, ponto)
          : girarNoChao(g.original, g.inicioChao, ponto)
      g.previa = encaixarTransformacao(
        g.previa,
        g.modo === 'mover' ? atuais.current.passoPosicaoM : null,
        g.modo === 'girar' ? atuais.current.passoGiroGraus : null,
      )
      g.moveu = true
      aplicarPrevia(g.id, g.previa)
    }
    const soltar = (e: PointerEvent) => {
      toques.current.delete(e.pointerId)
      if (gesto.current?.pointerId === e.pointerId) terminar(false)
    }
    const cancelar = (e: PointerEvent) => {
      toques.current.delete(e.pointerId)
      if (gesto.current?.pointerId === e.pointerId) terminar(true)
      pressao.current = null
    }
    const perderFoco = () => {
      terminar(true)
      toques.current.clear()
      pressao.current = null
    }
    const teclado = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      terminar(true)
      atuais.current.aoCancelarPosicionamento?.()
    }
    const sair = () => {
      if (previaPosicao.current) previaPosicao.current.visible = false
    }
    canvas.addEventListener('pointerdown', pressionar, true)
    canvas.addEventListener('pointerleave', sair)
    window.addEventListener('pointermove', mover)
    window.addEventListener('pointerup', soltar)
    window.addEventListener('pointercancel', cancelar)
    window.addEventListener('blur', perderFoco)
    window.addEventListener('keydown', teclado)
    return () => {
      canvas.removeEventListener('pointerdown', pressionar, true)
      canvas.removeEventListener('pointerleave', sair)
      window.removeEventListener('pointermove', mover)
      window.removeEventListener('pointerup', soltar)
      window.removeEventListener('pointercancel', cancelar)
      window.removeEventListener('blur', perderFoco)
      window.removeEventListener('keydown', teclado)
      terminar(true)
    }
  }, [gl, pontoNoChao, aplicarPrevia, terminar])

  useEffect(() => {
    const g = gesto.current
    if (!g) return
    const o = props.objetos.find((o) => o.id === g.id)
    if (!o || o.bloqueado || o.oculto || props.posicionando) terminar(true)
  }, [props.objetos, props.posicionando, terminar])

  return { grupos, alca, previaPosicao, iniciar, foiClique, pontoNoChao }
}
